/**
 * <s-header-search> — inline header search for desktop.
 *
 * Wraps a real GET form to the search page. While typing, product suggestions
 * from /search/suggest.json drop down right under the field, so search stays in
 * the header instead of opening a dialog. Enter (or "View all results")
 * goes to the full search page. Arrow keys move through suggestions; Escape or
 * clicking elsewhere closes them.
 *
 * Expects: input[name="q"], [data-header-search-panel], [data-header-search-list],
 * <template data-header-search-template="option|empty|view-all">,
 * data-no-results label ("__TERMS__" placeholder).
 */
class SHeaderSearch extends HTMLElement {
  #timer = 0;
  /** @type {AbortController | null} */
  #request = null;
  #active = -1;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="q"]'));
    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-header-search-panel]'));
    this.list = /** @type {HTMLElement | null} */ (this.querySelector('[data-header-search-list]'));
    this.form = this.querySelector('form');
    this.input?.addEventListener('input', this.#onInput);
    this.input?.addEventListener('keydown', this.#onKeydown);
    this.input?.addEventListener('focus', this.#onFocus);
    document.addEventListener('pointerdown', this.#onOutside);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    this.input?.removeEventListener('keydown', this.#onKeydown);
    this.input?.removeEventListener('focus', this.#onFocus);
    document.removeEventListener('pointerdown', this.#onOutside);
    this.#request?.abort();
  }

  #onInput = () => {
    clearTimeout(this.#timer);
    const terms = this.input?.value.trim() || '';
    if (!terms) {
      this.#close();
      return;
    }
    this.#timer = window.setTimeout(() => this.#fetch(terms), 250);
  };

  #onFocus = () => {
    if (this.list?.childElementCount && this.input?.value.trim()) this.#open();
  };

  /** @param {PointerEvent} event */
  #onOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const options = this.#options();
    if (event.key === 'Escape') {
      this.#close();
      return;
    }
    if (!options.length || this.panel?.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#setActive((this.#active + step + options.length) % options.length);
    } else if (event.key === 'Enter' && this.#active >= 0) {
      event.preventDefault();
      options[this.#active]?.click();
    }
  };

  #options() {
    return /** @type {HTMLAnchorElement[]} */ ([...(this.list?.querySelectorAll('[role="option"]') ?? [])]);
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.#options();
    this.#active = index;
    options.forEach((option, i) => {
      const selected = i === index;
      option.setAttribute('aria-selected', String(selected));
      option.toggleAttribute('data-active', selected);
      if (selected) {
        this.input?.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
    if (index < 0) this.input?.removeAttribute('aria-activedescendant');
  }

  #open() {
    if (!this.panel) return;
    this.panel.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
  }

  #close() {
    if (!this.panel) return;
    this.panel.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  /** @param {string} terms */
  async #fetch(terms) {
    this.#request?.abort();
    this.#request = new AbortController();
    const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
    const url = new URL(`${root}search/suggest.json`, location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) return;
      const data = await response.json();
      if (terms !== this.input?.value.trim()) return;
      this.#render(terms, data?.resources?.results?.products ?? []);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') this.#close();
    }
  }

  /**
   * Clones a <template data-header-search-template="name"> from the markup, so
   * every class used here is compiled from Liquid.
   * @param {string} name
   */
  #clone(name) {
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector(`template[data-header-search-template="${name}"]`));
    const node = template?.content.firstElementChild?.cloneNode(true);
    return /** @type {HTMLElement | null} */ (node ?? null);
  }

  /**
   * @param {string} terms
   * @param {Array<{ title: string, url: string, image?: string, featured_image?: { url?: string } }>} products
   */
  #render(terms, products) {
    if (!this.list) return;
    this.#active = -1;
    /** @type {HTMLElement[]} */
    const items = [];
    products.forEach((product, index) => {
      const option = this.#clone('option');
      if (!option) return;
      option.setAttribute('href', product.url);
      option.id = `header-search-option-${index}`;

      const img = /** @type {HTMLImageElement | null} */ (option.querySelector('img'));
      const imageUrl = product.featured_image?.url || product.image;
      if (img && imageUrl) {
        const src = new URL(imageUrl, location.origin);
        src.searchParams.set('width', '80');
        img.src = src.href;
      } else {
        img?.remove();
      }

      const [artist, ...rest] = product.title.replace(' – ', ' - ').split(' - ');
      const artistEl = option.querySelector('[data-artist]');
      const releaseEl = option.querySelector('[data-release]');
      if (artistEl) artistEl.textContent = rest.length ? artist : product.title;
      if (releaseEl) {
        if (rest.length) releaseEl.textContent = rest.join(' - ');
        else releaseEl.remove();
      }
      items.push(option);
    });

    if (!items.length) {
      const empty = this.#clone('empty');
      if (empty) empty.textContent = (this.dataset.noResults || '').replace('__TERMS__', terms);
      this.list.replaceChildren(...(empty ? [empty] : []));
    } else {
      const viewAll = this.#clone('view-all');
      if (viewAll) {
        const searchUrl = new URL(this.form?.getAttribute('action') || '/search', location.origin);
        searchUrl.searchParams.set('q', terms);
        viewAll.setAttribute('href', searchUrl.pathname + searchUrl.search);
      }
      this.list.replaceChildren(...items, ...(viewAll ? [viewAll] : []));
    }
    this.#open();
  }
}

if (!customElements.get('s-header-search')) {
  customElements.define('s-header-search', SHeaderSearch);
}
